import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../auth/AuthContext';
import { auth as authApi } from '../../services/api/auth';

/**
 * Login page: auto-redirects to Bifrost (Keycloak) for authentication.
 * If the user lacks the required role (permissionDenied), shows a permission-denied message
 * instead of redirecting, to avoid an infinite loop.
 *
 * A network failure while fetching the login URL (common right after the machine wakes and
 * the network stack is still coming up) is treated as transient: we show a "reconnecting"
 * state and retry automatically once connectivity returns, rather than the misleading
 * "check the Keycloak redirect URI" configuration hint.
 */
function LoginForm() {
    // error: null | { message: string, transient: boolean }
    const [error, setError] = useState(null);
    const { permissionDenied, clearPermissionDenied, checking } = useAuth();

    const redirectToBifrost = useCallback(async () => {
        setError(null);
        try {
            const newPath =
                window.location.pathname + (window.location.search.length < 4000 ? window.location.search : '');
            const url = await authApi.getLoginUrl(newPath);
            clearPermissionDenied();
            window.location.href = url;
        } catch (e) {
            const transient = authApi.isTransientFailure(e);
            setError({
                transient,
                message: transient
                    ? 'Connection lost. Reconnecting…'
                    : e?.response?.data?.detail ||
                      e?.message ||
                      'Could not start sign-in. Check backend Bifrost settings.',
            });
        }
    }, [clearPermissionDenied]);

    useEffect(() => {
        if (permissionDenied || checking) return;
        void redirectToBifrost();
    }, [permissionDenied, checking, redirectToBifrost]);

    // While in a transient (network) error state, retry the moment connectivity is likely
    // back — the network came online, or the tab became visible again after wake/unlock.
    useEffect(() => {
        if (!error?.transient) return undefined;
        const retry = () => void redirectToBifrost();
        const onVisible = () => {
            if (document.visibilityState === 'visible') retry();
        };
        window.addEventListener('online', retry);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('online', retry);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [error?.transient, redirectToBifrost]);

    if (permissionDenied) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100vw',
                    minHeight: '100vh',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        px: 2,
                        textAlign: 'center',
                        maxWidth: 420,
                    }}
                >
                    <Typography variant='h4' color='text.primary' component='h1' sx={{ fontWeight: 600 }}>
                        Mimir
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                        {process.env.REACT_APP_ENV || 'development'}
                    </Typography>
                    <Typography variant='h6' color='text.primary'>
                        Access denied
                    </Typography>
                    <Typography color='text.secondary'>
                        You have signed in successfully, but your account does not have the required role to access this
                        application. Contact your administrator if you believe this is an error.
                    </Typography>
                    <Button variant='contained' onClick={redirectToBifrost}>
                        Try again
                    </Button>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    gap: 2,
                    px: 2,
                }}
            >
                {error.transient ? (
                    <>
                        <CircularProgress />
                        <Typography color='text.secondary' textAlign='center'>
                            {error.message}
                        </Typography>
                        <Button variant='outlined' onClick={redirectToBifrost}>
                            Retry now
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography color='error' textAlign='center'>
                            {error.message}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' textAlign='center'>
                            Ensure Keycloak allows redirect URI:{' '}
                            <code>{`${
                                typeof window !== 'undefined' ? window.location.origin : ''
                            }/auth/callback`}</code>
                        </Typography>
                    </>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
            }}
        >
            <CircularProgress />
            <Typography color='text.secondary' textAlign='center'>
                Redirecting to sign in…
            </Typography>
        </Box>
    );
}
export default LoginForm;
